import { AnyAction } from 'redux';
import {
  DeltafaktorenChanged,
  GlobalActions,
  isMaklerbetreuerMaklerVttelOrVtgev,
  RbdMaxReceived,
  RoleDeterminedPayload,
  SetUnderwriterInfoPayload,
  TaasNachlassVorteilReceivedPayload,
  TarifgenerationData,
  TreuebonusMaxReceived,
  VereinbarungenChanged,
  VertragslaufzeitChanged,
  VorbelegungPayload
} from '@ps20-frontend/shop-integration-redux';
import cloneDeep from 'lodash/cloneDeep';
import {
  App,
  BausteinAnpassungsfaktoren,
  Deltafaktoren,
  TaasInfo,
  TaasTarifrisikoschluessel,
  TkvV2Vorbelegungdaten,
  Vorbelegungdaten
} from '@ps20-frontend/frontend-models';
import { VorvertragBaustein } from '@ps20/bff-models';

export const taasInfoReducer = function(
  sectionState: TaasInfo = null,
  action: AnyAction,
  appId: App,
  angebotsId?: string
): TaasInfo {
  // NOSONAR
  switch (action.type) {
    case GlobalActions.NEW_HASHED_TAAS_REQUEST: {
      const newState: TaasInfo = cloneDeep(sectionState);
      if (action.payload.appId === appId) {
        newState.hashedTaasRequest = action.payload.hash;
      }
      return newState;
    }
    case GlobalActions.CHANGE_RELEVANT_RISKDATA_HASH: {
      const payload: { hash: string } = action.payload;
      return {
        ...sectionState,
        hashedRiskData: payload.hash,
      };
    }
    case GlobalActions.RBD_MAX_RECEIVED: {
      const actionPayload: RbdMaxReceived = action.payload;
      if (actionPayload.appId === appId && actionPayload.angebotsId === angebotsId) {
        const rbdNachlass = isMaklerbetreuerMaklerVttelOrVtgev(actionPayload.withRole)
          ? actionPayload.rbdMax
          : 0;
        return { ...sectionState, rbdNachlass };
      }
      return sectionState;
    }
    case GlobalActions.ZUSCHLAGRISIKO_RECEIVED: {
      const payload = action.payload;
      if (payload.appId === appId && payload.angebotsId === angebotsId) {
        return { ...sectionState, zuschlagRisiko: payload.zuschlagRisiko };
      }

      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_REQUESTED: {
      const actionPayload: VorbelegungPayload = action.payload;
      const { appId, angebotsId } = actionPayload;
      if (actionPayload.appId === appId && actionPayload.angebotsId === angebotsId) {
        const vorbelegungdaten: TkvV2Vorbelegungdaten =
          actionPayload.vorhandeneVorbelegungdaten as TkvV2Vorbelegungdaten;
        return { ...sectionState, zuschlagRisikoVorvertrag: vorbelegungdaten?.zuschlagRisiko };
      }
      return sectionState;
    }
    case GlobalActions.ZUSCHLAEGEFAKTOR_RECEIVED: {
      const payload = action.payload;
      if (payload.appId === appId && payload.angebotsId === angebotsId) {
        return {
          ...sectionState,
          zuschlaegeFaktor: payload.zuschlaegeFaktor,
        };
      }
      return sectionState;
    }
    case GlobalActions.VEREINBARUNGEN_CHANGED: {
      const newState: TaasInfo = cloneDeep(sectionState);
      const actionPayload: VereinbarungenChanged = action.payload;
      const rbdPayload = actionPayload.rbdChanges.find(elem => {
        return elem.appId === appId && elem.angebotsId === angebotsId;
      });
      const tbPayload = actionPayload.tbChanges.find(elem => {
        return elem.appId === appId && elem.angebotsId === angebotsId;
      });
      const productVorteilsrabatt = actionPayload.vorteilsRabattArray.find(
        el => el.appId === appId && el.angebotsId === angebotsId,
      );
      if (rbdPayload) {
        newState.rbdNachlass = rbdPayload.rbd;
      }
      if (tbPayload) {
        newState.tbNachlass = tbPayload.treuebonus;
      }
      if (productVorteilsrabatt) {
        newState.vorteilsrabatt = productVorteilsrabatt.rabattInProzent;
      }
      return newState;
    }
    case GlobalActions.ROLE_DETERMINED: {
      const actionPayload: RoleDeterminedPayload = action.payload;
      return { ...sectionState, role: actionPayload.role };
    }
    case GlobalActions.TAAS_NACHLASS_RABATT_RECEIVED: {
      const payload = action.payload as TaasNachlassVorteilReceivedPayload;
      if (
        payload.appId === appId &&
        payload.angebotsId === angebotsId &&
        payload.taasNachlassVorteil?.nachlass &&
        isMaklerbetreuerMaklerVttelOrVtgev(sectionState.role)
      ) {
        return {
          ...sectionState,
          vorteilsrabatt: payload.taasNachlassVorteil?.nachlass,
        };
      }
      return sectionState;
    }
    case GlobalActions.VERTRAGSLAUFZEIT_CHANGED: {
      const actionPayload: VertragslaufzeitChanged = action.payload;
      if (
        actionPayload.appId === appId &&
        actionPayload.angebotsId === angebotsId &&
        actionPayload.vertragslaufzeit === 1
      ) {
        return {
          ...sectionState,
          tbNachlass: 0,
        };
      }
      return sectionState;
    }
    case GlobalActions.TREUEBONUS_MAX_RECEIVED: {
      const actionPayload: TreuebonusMaxReceived = action.payload;
      if (actionPayload.appId === appId && actionPayload.angebotsId === angebotsId) {
        return { ...sectionState, tbNachlass: 0 };
      }
      return sectionState;
    }
    case GlobalActions.DELTAFAKTOREN_RECEIVED: {
      const actionPayload: DeltafaktorenChanged = action.payload;
      return { ...sectionState, deltafaktoren: actionPayload.deltafaktoren };
    }
    case GlobalActions.TARIFGENERATION_DATA_RECEIVED: {
      const actionPayload: TarifgenerationData = action.payload;
      return {
        ...sectionState,
        deltafaktoren: {
          ...sectionState.deltafaktoren,
          salesProdukt: actionPayload?.salesProdukt,
          vorgangsId: actionPayload?.vorgangsId,
        },
      };
    }
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA: {
      if (action.payload) {
        return { ...sectionState, tbNachlass: 0, rbdNachlass: 0 };
      }
      return sectionState;
    }
    case GlobalActions.VORBELEGUNG_FOR_BEIBEHALTUNG_REQUESTED: {
      const actionPayload: Vorbelegungdaten = action.payload;
      if (actionPayload.forApp === appId && actionPayload.angebotsId === angebotsId) {
        return {
          ...sectionState,
          tbNachlass: actionPayload.nachlassTb,
          rbdNachlass: actionPayload.nachlassRbd,
          aktionsnachlass: actionPayload.aktionsNachlass,
          deltafaktoren: mapDeltafaktoren(actionPayload.vorvertragBausteinList),
          bausteinAnpassungsfaktoren: mapBausteinAnpassungsfaktoren(
            actionPayload.vorvertragBausteinList,
          ),
          nachlaesseFaktor: actionPayload.nachlaesseFaktor,
          vorteilsrabatt: actionPayload.vorteilsrabatt,
          zuschlaegeFaktor: actionPayload.zuschlaegeFaktor,
          anpassungsFaktor: actionPayload.anpassungsFaktor,
          wirksamkeitsBeginn: actionPayload.wirksamkeitsbeginn || actionPayload.aenderungdatum,
        };
      }
      return sectionState;
    }
    case GlobalActions.INTERNER_VERTRAGSBEZUG_RECEIVED: {
      if (appId === action.payload.appId && action.payload.angebotsId === angebotsId) {
        return {
          ...sectionState,
          internerVertragsbezugs: action.payload.internerVertragsbezugs,
        };
      }
      return sectionState;
    }
    case GlobalActions.UW_RESUBMISSION_FULFILLED_ANGEBOTE_COLLECTED: {
      const angebotsIds: Array<string> = action.payload?.angebotsIds || [];
      if (angebotsIds.includes(angebotsId)) {
        return {
          ...sectionState,
          zuschlagRisiko: undefined,
          zuschlaegeFaktor: 1,
        };
      }
      return sectionState;
    }
    case GlobalActions.SET_UNDERWRITER_INFO: {
      const payload: SetUnderwriterInfoPayload = action.payload;
      if (payload?.angebotsId !== angebotsId || payload.promoteOnly) {
        return sectionState;
      }
      if (isAnyUwContextStillRequired(payload)) {
        return sectionState;
      }
      return {
        ...sectionState,
        zuschlagRisiko: undefined,
        zuschlaegeFaktor: 1,
      };
    }
    default:
      return sectionState;
  }
};

function isAnyUwContextStillRequired(payload: SetUnderwriterInfoPayload): boolean {
  return Object.values(payload.requiredByContext ?? {}).some(Boolean);
}

function mapDeltafaktoren(
  bausteine: VorvertragBaustein<TaasTarifrisikoschluessel>[]
): Deltafaktoren {
  return {
    deltafaktoren: bausteine?.map(value => ({
      baustein: value.taasTarifrisikoschluessel,
      faktor: value.deltafaktor,
      instanzId: value.instanzId
    })),
    plzFaktor: bausteine?.find(baustein => baustein.instanzId === 0)?.plzFaktor
  } as Deltafaktoren;
}

function mapBausteinAnpassungsfaktoren(
  bausteine: VorvertragBaustein<TaasTarifrisikoschluessel>[]
): BausteinAnpassungsfaktoren {
  return {
    bausteinAnpassungsfaktoren: bausteine?.map(value => ({
      instanzId: value.instanzId,
      bausteinAnpassungsfaktor: value.bausteinAnpassungsfaktor
    }))
  } as BausteinAnpassungsfaktoren;
}
